import React, { useEffect } from 'react';

import {
  AppBar,
  Box,
  Button,
  Chip,
  Container,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material';

import {
  ArrowForward,
  Dashboard,
  DataObject,
  Devices,
  Language,
  Phone,
  SmartToy,
  WhatsApp,
  Web,
  CheckCircle,
} from '@mui/icons-material';

/* =========================================================
   DATA
========================================================= */

const services = [
  {
    icon: <Web />,
    title: 'Website & Web App',
    description:
      'Company profile, dashboard, booking, marketplace, sistem internal, hingga aplikasi web custom.',
  },
  {
    icon: <Devices />,
    title: 'Mobile Application',
    description:
      'Aplikasi mobile untuk customer, operasional, sales, delivery, maupun kebutuhan internal perusahaan.',
  },
  {
    icon: <Dashboard />,
    title: 'Custom Business System',
    description:
      'Software yang dirancang mengikuti alur kerja bisnis Anda, bukan memaksa bisnis mengikuti software.',
  },
  {
    icon: <SmartToy />,
    title: 'AI & WhatsApp',
    description:
      'Integrasikan AI dengan WhatsApp agar owner dapat bertanya dan mendapatkan informasi bisnis secara langsung.',
  },
  {
    icon: <DataObject />,
    title: 'Dashboard & Analytics',
    description:
      'Dashboard untuk penjualan, profit, stok, transaksi, performa, dan berbagai kebutuhan analitik.',
  },
  {
    icon: <Language />,
    title: 'API & Integration',
    description:
      'Integrasi API, payment gateway, WhatsApp, marketplace, database, maupun sistem existing.',
  },
];

const projects = [
  {
    number: '01',
    title: 'Fleet & Expedition System',
    category: 'Business Management',
    description:
      'Sistem untuk mengelola kendaraan, transaksi, finance, profit hingga AI analyst.',
    tags: ['Fleet', 'Finance', 'AI'],
  },
  {
    number: '02',
    title: 'POS & Cafe Management',
    category: 'Point of Sale',
    description:
      'POS dengan product variation, recipe, stock bahan, transaksi, customer dan laporan.',
    tags: ['POS', 'Inventory', 'Report'],
  },
  {
    number: '03',
    title: 'AI Business Assistant',
    category: 'AI Integration',
    description:
      'Owner dapat bertanya melalui WhatsApp dan AI mengambil serta menganalisis data bisnis.',
    tags: ['AI', 'WhatsApp', 'Analytics'],
  },
];

const technologies = [
  'React',
  'Node.js',
  'Express',
  'MongoDB',
  'REST API',
  'WebSocket',
  'WhatsApp API',
  'AI Integration',
  'Docker',
  'Kubernetes',
  'Artificial Intelligence',
];

/* =========================================================
   CONSTANTS
========================================================= */

const PURPLE = '#8b5cf6';
const PURPLE_LIGHT = '#a78bfa';

const TEXT_PRIMARY = '#ffffff';
const TEXT_SECONDARY = 'rgba(255,255,255,.58)';
const TEXT_MUTED = 'rgba(255,255,255,.40)';
const TEXT_FAINT = 'rgba(255,255,255,.25)';

const BORDER = 'rgba(255,255,255,.08)';
const CARD_BG = 'rgba(255,255,255,.025)';

/* =========================================================
   SECTION TITLE
========================================================= */

const SectionTitle = ({
  eyebrow,
  title,
  description,
  align = 'left',
}) => {
  return (
    <Box
      sx={{
        maxWidth: 760,
        mb: {
          xs: 5,
          md: 6,
        },
        textAlign: align,
      }}
    >
      <Typography
        sx={{
          color: PURPLE,
          fontSize: 12,
          fontWeight: 800,
          letterSpacing: 2,
          textTransform: 'uppercase',
          mb: 1.8,
        }}
      >
        {eyebrow}
      </Typography>

      <Typography
        component="h2"
        sx={{
          fontWeight: 800,
          fontSize: {
            xs: 34,
            sm: 42,
            md: 52,
          },
          lineHeight: 1.08,
          letterSpacing: {
            xs: '-1.5px',
            md: '-2.5px',
          },
        }}
      >
        {title}
      </Typography>

      {description && (
        <Typography
          sx={{
            color: TEXT_SECONDARY,
            mt: 2.2,
            fontSize: 15,
            lineHeight: 1.8,
            maxWidth: 680,
          }}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

/* =========================================================
   SERVICE CARD
========================================================= */

const ServiceCard = ({ service }) => {
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: {
          xs: 250,
          md: 275,
        },

        p: {
          xs: 2.8,
          md: 3.2,
        },

        borderRadius: 3,
        bgcolor: CARD_BG,
        border: `1px solid ${BORDER}`,

        display: 'flex',
        flexDirection: 'column',

        transition:
          'transform .25s ease, border-color .25s ease, background .25s ease',

        '&:hover': {
          transform: 'translateY(-5px)',
          borderColor: 'rgba(139,92,246,.35)',
          bgcolor: 'rgba(139,92,246,.04)',
        },
      }}
    >
      <Box
        sx={{
          width: 46,
          height: 46,
          flexShrink: 0,

          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',

          borderRadius: 2,
          bgcolor: 'rgba(139,92,246,.10)',
          color: PURPLE_LIGHT,

          mb: 3,
        }}
      >
        {service.icon}
      </Box>

      <Typography
        sx={{
          fontSize: 18,
          fontWeight: 750,
          lineHeight: 1.3,
          mb: 1.3,
        }}
      >
        {service.title}
      </Typography>

      <Typography
        sx={{
          color: TEXT_MUTED,
          fontSize: 14,
          lineHeight: 1.75,
        }}
      >
        {service.description}
      </Typography>
    </Box>
  );
};

/* =========================================================
   PROJECT CARD
========================================================= */

const ProjectCard = ({ project }) => {
  return (
    <Box
      sx={{
        height: '100%',
        minHeight: {
          xs: 360,
          md: 390,
        },

        p: {
          xs: 2.8,
          md: 3.2,
        },

        borderRadius: 3,
        bgcolor: '#09090c',
        border: `1px solid ${BORDER}`,

        display: 'flex',
        flexDirection: 'column',

        transition:
          'transform .25s ease, border-color .25s ease',

        '&:hover': {
          transform: 'translateY(-5px)',
          borderColor: 'rgba(139,92,246,.35)',
        },
      }}
    >
      {/* TOP */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography
          sx={{
            color: TEXT_FAINT,
            fontSize: 13,
            fontWeight: 800,
          }}
        >
          {project.number}
        </Typography>

        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: `1px solid ${BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ArrowForward
            sx={{
              fontSize: 16,
              color: TEXT_FAINT,
            }}
          />
        </Box>
      </Stack>

      {/* CONTENT */}
      <Box sx={{ mt: 7 }}>
        <Typography
          sx={{
            color: PURPLE,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: 1.5,
            textTransform: 'uppercase',
            mb: 1,
          }}
        >
          {project.category}
        </Typography>

        <Typography
          component="h3"
          sx={{
            fontSize: {
              xs: 21,
              md: 23,
            },
            fontWeight: 800,
            lineHeight: 1.25,
            mb: 1.5,
          }}
        >
          {project.title}
        </Typography>

        <Typography
          sx={{
            color: TEXT_MUTED,
            fontSize: 14,
            lineHeight: 1.75,
          }}
        >
          {project.description}
        </Typography>
      </Box>

      {/* TAGS */}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={0.8}
        sx={{
          mt: 'auto',
          pt: 4,
        }}
      >
        {project.tags.map((tag) => (
          <Chip
            key={tag}
            label={tag}
            size="small"
            sx={{
              height: 28,
              bgcolor: 'rgba(255,255,255,.04)',
              color: 'rgba(255,255,255,.48)',
              border:
                '1px solid rgba(255,255,255,.07)',

              '& .MuiChip-label': {
                px: 1.1,
                fontSize: 11,
              },
            }}
          />
        ))}
      </Stack>
    </Box>
  );
};

/* =========================================================
   MAIN
========================================================= */

export default function DeveloperProfile() {
  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
  };

  useEffect(() => {
    document.title =
      'Custom Program @dcar.developer';
  }, []);

  return (
    <>
      <CssBaseline />

      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#050507',
          color: TEXT_PRIMARY,
          overflow: 'hidden',

          '& *': {
            boxSizing: 'border-box',
          },
        }}
      >
        {/* =====================================================
            NAVBAR
        ===================================================== */}

        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            bgcolor: 'rgba(5,5,7,.72)',
            backdropFilter: 'blur(18px)',
            borderBottom: `1px solid ${BORDER}`,
          }}
        >
          <Container maxWidth="lg">
            <Toolbar
              disableGutters
              sx={{
                height: {
                  xs: 64,
                  md: 72,
                },
                justifyContent:
                  'space-between',
              }}
            >
              <Typography
                onClick={() =>
                  scrollTo('home')
                }
                sx={{
                  cursor: 'pointer',
                  fontSize: 24,
                  fontWeight: 900,
                  letterSpacing: '-2px',
                }}
              >
                DC
                <Box
                  component="span"
                  sx={{
                    color: PURPLE,
                  }}
                >
                  .
                </Box>
              </Typography>

              <Stack
                direction="row"
                spacing={0.5}
                sx={{
                  display: {
                    xs: 'none',
                    md: 'flex',
                  },
                }}
              >
                {[
                  ['Services', 'services'],
                  ['Projects', 'projects'],
                  ['About', 'about'],
                  ['Contact', 'contact'],
                ].map(([label, id]) => (
                  <Button
                    key={id}
                    onClick={() =>
                      scrollTo(id)
                    }
                    sx={{
                      minWidth: 'auto',
                      px: 1.5,
                      color:
                        'rgba(255,255,255,.55)',
                      textTransform: 'none',

                      '&:hover': {
                        color: '#fff',
                        bgcolor:
                          'transparent',
                      },
                    }}
                  >
                    {label}
                  </Button>
                ))}
              </Stack>

              <Button
                variant="outlined"
                onClick={() =>
                  scrollTo('contact')
                }
                sx={{
                  minWidth: 0,
                  px: 2,
                  borderColor:
                    'rgba(255,255,255,.18)',
                  color: '#fff',
                  borderRadius: 2,
                  textTransform: 'none',

                  display: {
                    xs: 'none',
                    sm: 'inline-flex',
                  },
                }}
              >
                Let's Talk
              </Button>
            </Toolbar>
          </Container>
        </AppBar>

        {/* =====================================================
            HERO
        ===================================================== */}

        <Box
          id="home"
          component="section"
          sx={{
            minHeight: {
              xs: 'auto',
              md: '100vh',
            },

            position: 'relative',

            display: 'flex',
            alignItems: 'center',

            pt: {
              xs: 12,
              md: 10,
            },

            pb: {
              xs: 8,
              md: 10,
            },

            background:
              'radial-gradient(circle at 78% 25%, rgba(139,92,246,.16), transparent 30%), radial-gradient(circle at 15% 80%, rgba(59,130,246,.08), transparent 25%)',
          }}
        >
          {/* Background glow */}
          <Box
            sx={{
              position: 'absolute',
              width: {
                xs: 300,
                md: 500,
              },
              height: {
                xs: 300,
                md: 500,
              },

              borderRadius: '50%',

              background:
                'radial-gradient(circle, rgba(139,92,246,.12), transparent 65%)',

              right: {
                xs: -180,
                md: -160,
              },

              top: {
                xs: 80,
                md: 100,
              },

              filter: 'blur(20px)',
              pointerEvents: 'none',
            }}
          />

          <Container maxWidth="lg">
            <Box
              sx={{
                position: 'relative',
                maxWidth: 900,

                py: {
                  xs: 5,
                  md: 8,
                },
              }}
            >
              <Chip
                icon={
                  <CheckCircle
                    sx={{
                      color:
                        '#7ee2a8 !important',
                      fontSize: 15,
                    }}
                  />
                }
                label="AVAILABLE FOR NEW PROJECTS"
                sx={{
                  mb: 3,
                  height: 30,

                  bgcolor:
                    'rgba(255,255,255,.04)',

                  border:
                    '1px solid rgba(255,255,255,.1)',

                  color:
                    'rgba(255,255,255,.55)',

                  fontSize: 10,
                  letterSpacing: 1.1,
                }}
              />

              <Typography
                component="h1"
                sx={{
                  fontSize: {
                    xs: 48,
                    sm: 64,
                    md: 82,
                  },

                  lineHeight: {
                    xs: 1.02,
                    md: 0.98,
                  },

                  fontWeight: 900,

                  letterSpacing: {
                    xs: '-3px',
                    md: '-5px',
                  },
                }}
              >
                I Build Software

                <Box
                  component="span"
                  sx={{
                    display: 'block',
                    color: TEXT_FAINT,
                  }}
                >
                  For Real Business.
                </Box>
              </Typography>

              <Typography
                sx={{
                  mt: 4,

                  maxWidth: 680,

                  color: TEXT_SECONDARY,

                  fontSize: {
                    xs: 16,
                    md: 18,
                  },

                  lineHeight: 1.75,
                }}
              >
                Full-stack developer yang membantu
                bisnis membangun website, aplikasi,
                sistem operasional, integrasi API,
                dan solusi AI sesuai kebutuhan bisnis.
              </Typography>

              {/* CTA */}
              <Stack
                direction={{
                  xs: 'column',
                  sm: 'row',
                }}
                spacing={1.5}
                sx={{
                  mt: 4,
                }}
              >
                <Button
                  variant="contained"
                  endIcon={
                    <ArrowForward />
                  }
                  onClick={() =>
                    scrollTo('contact')
                  }
                  sx={{
                    px: 3,
                    py: 1.4,

                    borderRadius: 2,

                    bgcolor: '#fff',
                    color: '#050507',

                    fontWeight: 700,
                    textTransform: 'none',

                    '&:hover': {
                      bgcolor: '#eee',
                    },
                  }}
                >
                  Konsultasikan Project
                </Button>

                <Button
                  variant="outlined"
                  onClick={() =>
                    scrollTo('projects')
                  }
                  sx={{
                    px: 3,
                    py: 1.4,

                    borderRadius: 2,

                    borderColor:
                      'rgba(255,255,255,.15)',

                    color: '#fff',

                    textTransform: 'none',

                    '&:hover': {
                      borderColor:
                        'rgba(255,255,255,.35)',
                    },
                  }}
                >
                  Lihat Project
                </Button>
              </Stack>

              {/* STATS */}
              <Grid
                container
                spacing={{
                  xs: 2,
                  sm: 0,
                }}
                sx={{
                  mt: 7,
                  pt: 3,

                  borderTop:
                    `1px solid ${BORDER}`,

                  maxWidth: 800,
                }}
              >
                {[
                  ['FULL-STACK', 'Development'],
                  ['CUSTOM', 'Business Software'],
                  ['AI', 'Integration'],
                  ['API', 'Integration'],
                ].map(
                  ([number, label]) => (
                    <Grid
                      key={label}
                      size={{
                        xs: 6,
                        sm: 3,
                      }}
                    >
                      <Box
                        sx={{
                          pr: {
                            xs: 1,
                            sm: 2,
                          },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 800,
                            fontSize: {
                              xs: 14,
                              md: 16,
                            },
                          }}
                        >
                          {number}
                        </Typography>

                        <Typography
                          sx={{
                            mt: 0.5,
                            color:
                              'rgba(255,255,255,.38)',
                            fontSize: 11,
                          }}
                        >
                          {label}
                        </Typography>
                      </Box>
                    </Grid>
                  ),
                )}
              </Grid>
            </Box>
          </Container>
        </Box>

        {/* =====================================================
            SERVICES
        ===================================================== */}

        <Box
          id="services"
          component="section"
          sx={{
            py: {
              xs: 9,
              md: 14,
            },

            borderTop:
              `1px solid ${BORDER}`,
          }}
        >
          <Container maxWidth="lg">
            <SectionTitle
              eyebrow="What I Build"
              title={
                <>
                  Software yang mengikuti{' '}
                  <Box
                    component="span"
                    sx={{
                      color: TEXT_FAINT,
                    }}
                  >
                    kebutuhan bisnis.
                  </Box>
                </>
              }
              description="Tidak semua bisnis membutuhkan software yang sama. Sistem dirancang berdasarkan proses dan kebutuhan bisnis Anda."
            />

            <Grid
              container
              spacing={{
                xs: 2,
                md: 2.5,
              }}
            >
              {services.map(
                (service) => (
                  <Grid
                    key={service.title}
                    size={{
                      xs: 12,
                      sm: 6,
                      md: 4,
                    }}
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <ServiceCard
                      service={service}
                    />
                  </Grid>
                ),
              )}
            </Grid>
          </Container>
        </Box>

        {/* =====================================================
            ABOUT
        ===================================================== */}

        <Box
          id="about"
          component="section"
          sx={{
            py: {
              xs: 9,
              md: 14,
            },

            bgcolor: '#08080b',

            borderTop:
              `1px solid ${BORDER}`,
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={{
                xs: 5,
                md: 10,
              }}
              alignItems="center"
            >
              <Grid
                size={{
                  xs: 12,
                  md: 5,
                }}
              >
                <SectionTitle
                  eyebrow="My Approach"
                  title={
                    <>
                      Bukan hanya{' '}
                      <Box
                        component="span"
                        sx={{
                          color: TEXT_FAINT,
                        }}
                      >
                        membuat aplikasi.
                      </Box>
                    </>
                  }
                />
              </Grid>

              <Grid
                size={{
                  xs: 12,
                  md: 7,
                }}
              >
                <Box
                  sx={{
                    maxWidth: 650,
                  }}
                >
                  <Typography
                    sx={{
                      color: '#ddd',

                      fontSize: {
                        xs: 22,
                        md: 27,
                      },

                      lineHeight: 1.45,

                      mb: 3,
                    }}
                  >
                    Saya melihat software sebagai
                    bagian dari proses bisnis, bukan
                    sekadar kumpulan fitur.
                  </Typography>

                  <Typography
                    sx={{
                      color: TEXT_MUTED,
                      fontSize: 15,
                      lineHeight: 1.9,
                      mb: 2,
                    }}
                  >
                    Sebelum membuat sistem, saya
                    memahami bagaimana bisnis bekerja:
                    bagaimana transaksi terjadi,
                    bagaimana data masuk, bagaimana
                    laporan dibuat, dan bagian mana
                    yang masih dilakukan secara manual.
                  </Typography>

                  <Typography
                    sx={{
                      color: TEXT_MUTED,
                      fontSize: 15,
                      lineHeight: 1.9,
                    }}
                  >
                    Dari sana, sistem dirancang untuk
                    membuat proses lebih terstruktur,
                    mengurangi pekerjaan manual, dan
                    memberikan informasi yang lebih
                    mudah digunakan.
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* =====================================================
            PROJECTS
        ===================================================== */}

        <Box
          id="projects"
          component="section"
          sx={{
            py: {
              xs: 9,
              md: 14,
            },

            borderTop:
              `1px solid ${BORDER}`,
          }}
        >
          <Container maxWidth="lg">
            <SectionTitle
              eyebrow="Selected Projects"
              title={
                <>
                  Beberapa jenis sistem{' '}
                  <Box
                    component="span"
                    sx={{
                      color: TEXT_FAINT,
                    }}
                  >
                    yang sudah saya bangun.
                  </Box>
                </>
              }
            />

            <Grid
              container
              spacing={{
                xs: 2,
                md: 2.5,
              }}
            >
              {projects.map(
                (project) => (
                  <Grid
                    key={project.number}
                    size={{
                      xs: 12,
                      md: 4,
                    }}
                    sx={{
                      display: 'flex',
                    }}
                  >
                    <ProjectCard
                      project={project}
                    />
                  </Grid>
                ),
              )}
            </Grid>
          </Container>
        </Box>

        {/* ================= TECHNOLOGY ================= */}

        <Box
          component="section"
          sx={{
            py: {
              xs: 8,
              sm: 10,
              md: 12,
            },
            bgcolor: '#08080b',
            borderTop: '1px solid rgba(255,255,255,.07)',
          }}
        >
          <Container maxWidth="lg">
            <Grid
              container
              spacing={{
                xs: 4,
                md: 8,
              }}
              alignItems="center"
            >
              {/* TITLE */}
              <Grid
                size={{
                  xs: 12,
                  md: 5,
                }}
              >
                <Typography
                  sx={{
                    color: '#8b5cf6',
                    fontSize: {
                      xs: 11,
                      sm: 12,
                    },
                    letterSpacing: {
                      xs: 1.5,
                      sm: 2,
                    },
                    fontWeight: 700,
                  }}
                >
                  TECHNOLOGY
                </Typography>

                <Typography
                  sx={{
                    mt: 1.5,

                    /*
                    * Responsive font
                    */
                    fontSize: {
                      xs: 34,
                      sm: 40,
                      md: 46,
                    },

                    /*
                    * Jangan terlalu negatif di mobile
                    */
                    letterSpacing: {
                      xs: '-1px',
                      sm: '-1.5px',
                      md: '-2px',
                    },

                    lineHeight: {
                      xs: 1.12,
                      md: 1.08,
                    },

                    fontWeight: 800,

                    /*
                    * Mencegah text keluar container
                    */
                    maxWidth: '100%',
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                  }}
                >
                  Modern technology.
                  
                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      color: 'rgba(255,255,255,.22)',

                      /*
                      * Pastikan baris kedua juga aman
                      */
                      whiteSpace: 'normal',
                    }}
                  >
                    Practical solutions.
                  </Box>
                </Typography>
              </Grid>

              {/* TECHNOLOGIES */}
              <Grid
                size={{
                  xs: 12,
                  md: 7,
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: {
                      xs: 1,
                      sm: 1.2,
                    },
                    width: '100%',
                  }}
                >
                  {technologies.map((technology) => (
                    <Chip
                      key={technology}
                      label={technology}
                      variant="outlined"
                      sx={{
                        height: {
                          xs: 34,
                          sm: 36,
                        },

                        maxWidth: '100%',

                        color: 'rgba(255,255,255,.55)',
                        borderColor: 'rgba(255,255,255,.1)',
                        borderRadius: 1.5,

                        '& .MuiChip-label': {
                          px: {
                            xs: 1.2,
                            sm: 1.5,
                          },

                          /*
                          * Chip panjang tidak membuat layout
                          * melebar di mobile
                          */
                          whiteSpace: 'normal',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>

        {/* =====================================================
            CTA
        ===================================================== */}

        <Box
          id="contact"
          component="section"
          sx={{
            py: {
              xs: 8,
              md: 14,
            },
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',

                textAlign: 'center',

                px: {
                  xs: 3,
                  sm: 5,
                  md: 8,
                },

                py: {
                  xs: 7,
                  md: 10,
                },

                borderRadius: 4,

                bgcolor: '#0a0a0e',

                border:
                  `1px solid ${BORDER}`,
              }}
            >
              {/* Glow */}
              <Box
                sx={{
                  position: 'absolute',

                  width: 500,
                  height: 500,

                  borderRadius: '50%',

                  background:
                    'radial-gradient(circle, rgba(139,92,246,.13), transparent 65%)',

                  left: '50%',
                  top: -320,

                  transform:
                    'translateX(-50%)',

                  pointerEvents: 'none',
                }}
              />

              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                }}
              >
                <Typography
                  sx={{
                    color: PURPLE,
                    fontSize: 11,
                    letterSpacing: 2,
                    fontWeight: 800,
                  }}
                >
                  HAVE A PROJECT?
                </Typography>

                <Typography
                  component="h2"
                  sx={{
                    fontSize: {
                      xs: 38,
                      sm: 48,
                      md: 60,
                    },

                    lineHeight: 1.05,

                    fontWeight: 900,

                    letterSpacing: {
                      xs: '-2px',
                      md: '-3px',
                    },

                    mt: 2,
                  }}
                >
                  Punya ide aplikasi?

                  <Box
                    component="span"
                    sx={{
                      display: 'block',
                      color: TEXT_FAINT,
                    }}
                  >
                    Mari kita bangun.
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    maxWidth: 600,

                    mx: 'auto',

                    mt: 3,
                    mb: 4,

                    color: TEXT_MUTED,

                    fontSize: 15,
                    lineHeight: 1.8,
                  }}
                >
                  Ceritakan kebutuhan atau masalah
                  bisnis Anda. Kita bisa mulai dari
                  diskusi sederhana sebelum menentukan
                  teknologi dan sistem yang dibutuhkan.
                </Typography>

                <Button
                  component="a"
                  href="https://wa.me/6289604884717"
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  endIcon={<WhatsApp />}
                  sx={{
                    bgcolor: '#fff',
                    color: '#050507',

                    px: 3,
                    py: 1.4,

                    borderRadius: 2,

                    fontWeight: 700,

                    textTransform: 'none',

                    '&:hover': {
                      bgcolor: '#eee',
                    },
                  }}
                >
                  Chat via WhatsApp
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* =====================================================
            FOOTER
        ===================================================== */}

        <Box
          component="footer"
          sx={{
            borderTop:
              `1px solid ${BORDER}`,
            py: 4,
          }}
        >
          <Container maxWidth="lg">
            <Stack
              direction={{
                xs: 'column',
                sm: 'row',
              }}
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              justifyContent="space-between"
              spacing={2}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: 24,
                    fontWeight: 900,
                    letterSpacing: '-2px',
                  }}
                >
                  DC
                  <Box
                    component="span"
                    sx={{
                      color: PURPLE,
                    }}
                  >
                    .
                  </Box>
                </Typography>

                <Typography
                  sx={{
                    color:
                      'rgba(255,255,255,.3)',
                    fontSize: 12,
                    mt: 0.5,
                  }}
                >
                  Full-Stack Developer ·
                  Custom Software
                </Typography>
              </Box>

              <IconButton
                component="a"
                href="https://wa.me/6289604884717"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color:
                    'rgba(255,255,255,.4)',

                  border:
                    '1px solid rgba(255,255,255,.08)',

                  '&:hover': {
                    color: '#fff',
                    bgcolor:
                      'rgba(139,92,246,.08)',
                  },
                }}
              >
                <Phone />
              </IconButton>
            </Stack>

            <Divider
              sx={{
                my: 3,
                borderColor:
                  'rgba(255,255,255,.06)',
              }}
            />

            <Typography
              sx={{
                color:
                  'rgba(255,255,255,.2)',
                fontSize: 11,
              }}
            >
              © {new Date().getFullYear()}{' '}
              Dcar Developer. All rights reserved.
            </Typography>
          </Container>
        </Box>
      </Box>
    </>
  );
}